import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { filter, Observable, Subscription, take } from 'rxjs'
import { MessageService } from 'primeng/api'

import { AllocatableLease } from '@modules/private/payment/components/lease-distribution-selector/lease-distribution-selector.component'
import { LEDGER_VIEW_LABELS, LeaseLedger, LedgerPeriod, PaymentRow, TwinEvidence } from '../../data/interfaces/lease-ledger.interface'
import { toAllocatableLeases } from '@modules/private/payment/components/lease-distribution-selector/lease-allocation.util'
import { PaymentDateTarget } from '@modules/private/payment/components/payment-date-dialog/payment-date-dialog.component'
import { AttentionGroup } from '@modules/private/payment/components/payment-adjust-modal/payment-adjust-modal.component'
import { ApplyPreviewDialogComponent, PreviewMode } from './apply-preview-dialog/apply-preview-dialog.component'
import { ApiApplicationService } from '@modules/private/application/data/services/api/api-application.service'
import { ApiInvoiceService } from '@modules/private/invoices/data/services/api/api-invoice.service'
import { ApiPaymentService } from '@modules/private/payment/data/services/api/api-payment.service'
import { PaymentService } from '@modules/private/payment/data/services/view/payment.service'
import { ApiLeaseService } from '@modules/private/lease/data/services/api/api-lease.service'
import { LeaseDetailService } from '../../data/services/view/lease-detail.service'
import { LeaseLedgerService } from '../../data/services/view/lease-ledger.service'
import { AuthService } from '@data/services/modules/auth/auth.service'

export type LedgerFilter = 'all' | 'overdue' | 'partial' | 'noinv' | 'rep'

@Component({
  selector: 'app-lease-enforceability',
  templateUrl: './lease-enforceability.component.html',
  styleUrls: ['./lease-enforceability.component.scss'],
})
export class LeaseEnforceabilityComponent implements OnInit, OnDestroy {
  //* Etiquetas por `view` del ledger. Viven junto al tipo: las pinta también el libro mayor que se
  //* abre desde Cobranza, y dos copias se separan a la primera corrección.
  statusLabels: Record<string, string> = LEDGER_VIEW_LABELS

  view: 'periods' | 'payments' = 'periods'
  readonly viewOptions = [
    { label: 'Por periodo', value: 'periods' },
    { label: 'Por pago', value: 'payments' },
  ]
  filter: LedgerFilter = 'all'
  readonly filters: { key: LedgerFilter; label: string }[] = [
    { key: 'all', label: 'Todas' },
    { key: 'overdue', label: 'Vencidas' },
    { key: 'partial', label: 'Con varios pagos' },
    { key: 'noinv', label: 'Sin factura' },
    { key: 'rep', label: 'REP pendiente' },
  ]
  search = ''
  //* Cuántas letras hay detrás de cada filtro. Va en el propio chip para no tener que abrirlo
  //* para averiguarlo; se cuenta sobre TODAS las letras, sin el término de búsqueda.
  filterCounts: Record<LedgerFilter, number> = { all: 0, overdue: 0, partial: 0, noinv: 0, rep: 0 }

  //* Las letras futuras a partir de la 14.ª se pliegan: en un contrato a 48 meses son ruido entre
  //* lo que sí se opera. Solo aplica a la vista completa (sin filtro ni búsqueda), que es donde
  //* estorban; el pie de la tabla ofrece revelarlas y la decisión se conserva.
  showFuture = false
  hiddenFutureCount = 0
  private static readonly VISIBLE_ROWS_BEFORE_FOLDING = 14

  //* Filas del esqueleto de la tabla: ocho bastan para llenar el alto visible del scroller.
  readonly skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8]

  ledger: LeaseLedger | null = null
  visiblePeriods: LedgerPeriod[] = []
  visiblePayments: PaymentRow[] = []
  //* Gemelos por pago (F18): salen de `unapplied[]` y la vista Por pago los pinta como badge.
  twinsById = new Map<string, TwinEvidence[]>()
  //* Se conserva entre refrescos porque la llave es `_id` y PrimeNG lo muta en sitio.
  expandedRowKeys: Record<string, boolean> = {}

  // Cajón del pago (F6)
  showDrawer = false
  drawerPayment: PaymentRow | null = null

  // Editor de saldos abierto desde la letra (F28): la letra ya elegida
  presetWaivePeriod: number | null = null

  // Ajuste del pago desde el cajón (`.claude/ajuste-de-pagos.md` de la api): anular una
  // carga repetida, cerrar un remanente o deshacer. El botón existía y no estaba conectado a nada.
  showAdjustModal = false
  adjustPayment: PaymentRow | null = null
  showDateDialog = false
  datePayment: PaymentDateTarget | null = null
  adjustGroup: AttentionGroup = 'listado'
  adjustPreviewing = false
  adjustSaving = false

  // Diálogo previsualizar → confirmar (F11-F14, F20)
  @ViewChild('previewDialog') previewDialog!: ApplyPreviewDialogComponent
  showPreview = false
  previewMode: PreviewMode = 'APPLY'
  previewPayments: PaymentRow[] = []
  /** Los arrendamientos activos del cliente, para el selector de reparto. Vacío = sin selector. */
  clientLeases: AllocatableLease[] = []
  previewApplication: { applicationId: string; n: number | null; letters?: number[]; amount: number } | null = null
  previewing = false
  savingPreview = false

  // Registrar pago desde la pestaña (F19) con el aviso de duplicado (F16)
  showRegister = false
  savingPayment = false
  confirmingCloseRegister = false
  duplicate: { twins: TwinEvidence[]; activeLeases: number } | null = null
  duplicateReason = ''

  showEditModal = false
  saving = false

  // Editor de saldos (POST /leases/:id/amounts). El flujo obliga a previsualizar antes de guardar.
  showAmountsModal = false
  previewingAmounts = false
  savingAmounts = false

  //* Historial e índice de facturas por letra: se derivan del ledger, que ya trae `invoice.active` e
  //* `invoice.history` por periodo. Antes salían de un `getByLease` aparte; el libro mayor los incluye
  //* y la pestaña hace UNA sola llamada (F2).
  private historyByPeriodNum = new Map<string, any[]>()
  private invoiceById = new Map<string, any>()
  loadingInvoices = false
  invoicesLoaded = false
  downloadingKey: string | null = null

  // Modal Refacturar
  showRefacturarModal = false
  refacturarInvoiceId: string | null = null
  refacturarCurrentFolioFiscal = ''
  refacturarNewFolioFiscal = ''
  refacturarReason = ''
  refacturarPdf: File | null = null
  refacturarXml: File | null = null
  refacturando = false

  // Modal Cancelar
  showCancelModal = false
  cancelInvoiceId: string | null = null
  cancelFolioFiscal = ''
  cancelReason = ''
  cancelando = false

  // Modal Historial de facturación del periodo
  showHistoryModal = false
  historyPeriodNumber: number | null = null
  historyInvoices: any[] = []

  // Modal Cargar factura del periodo (carga unitaria PDF/XML)
  showUploadModal = false
  uploadPeriod: any = null
  uploadPdf: File | null = null
  uploadXml: File | null = null
  uploadingInvoice = false

  // Modal Complementos de pago (REP) del periodo — reutiliza app-invoice-complements-dialog.
  showComplementsModal = false
  complementsInvoice: any = null

  private subs: Subscription[] = []

  constructor(
    public leaseDetailService: LeaseDetailService,
    public ledgerService: LeaseLedgerService,
    private messageService: MessageService,
    private apiInvoiceService: ApiInvoiceService,
    private apiApplicationService: ApiApplicationService,
    public paymentService: PaymentService,
    private apiPaymentService: ApiPaymentService,
    private authService: AuthService,
    private apiLease: ApiLeaseService,
  ) {}

  ngOnInit(): void {
    //* El tabView vive bajo `*ngIf="leaseDetailService.data"`: aquí `id` ya existe. Desaparece el
    //* reintento de 25 × 200 ms que hacía falta cuando las facturas se pedían por separado.
    this.subs.push(
      this.ledgerService.ledger$.subscribe((ledger) => {
        this.ledger = ledger
        this.indexInvoices()
        this.recompute()
      }),
    )
    this.ledgerService.load(this.leaseDetailService.id)
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe())
    this.ledgerService.clear()
  }

  get data() {
    return this.leaseDetailService.data.quotation as any
  }

  private get userId(): string | undefined {
    return this.authService.getUser?.user?.id
  }

  // Letras ordenadas por FECHA. Es lo que ancla el recorrido del editor de fechas — tienen que ser la
  // misma lista que pinta la tabla: en los arrendamientos migrados con rentas parciales los períodos se
  // numeran 1.1, 1.2, … y como número 1 < 1.1, así que ordenar por número deja primera a la letra 1,
  // que ahí cae meses DESPUÉS de la primera renta parcial (B-011).
  //
  // Se memoiza contra la referencia del arreglo de origen: los consumidores lo leen en cada ciclo de
  // detección de cambios y un arreglo nuevo cada vez los haría repintar sin parar.
  private chronoCacheSource: any[] | null = null
  private chronoCache: any[] = []
  get chronologicalPeriods(): any[] {
    const periods = this.data?.amortizationPeriods || []
    if (periods !== this.chronoCacheSource) {
      this.chronoCacheSource = periods
      // Una letra sin fecha se va al final en vez de desaparecer.
      const key = (p: any) => (p?.date ? new Date(p.date).getTime() : Number.POSITIVE_INFINITY)
      this.chronoCache = [...periods].sort((a, b) => key(a) - key(b))
    }
    return this.chronoCache
  }

  // Fecha de la primera letra como Date a medianoche LOCAL del día UTC (evita el desfase -1 en p-calendar)
  get firstPeriodDate(): Date | null {
    const first = this.chronologicalPeriods[0]
    if (!first?.date) return null
    const d = new Date(first.date)
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
  }

  get paidPeriodsCount(): number {
    return this.chronologicalPeriods.filter((p: any) => p?.status === 'PAID' || p?.status === 'ANTICIPATED').length
  }

  get amountsPeriods(): any[] {
    return this.chronologicalPeriods
  }

  /**
   * Lo que el editor de saldos toma como «de dónde parte el arrendamiento»: los saldos GUARDADOS en el
   * arrendamiento (`wallet.*`, `rents.*`) más las sumas de su tabla.
   *
   *! Antes solo traía las sumas de la tabla. El modal lee `walletValue`, `rentsToCollectAmount` y
   *! `rentsToCollect` para sus tarjetas de contexto, para la columna «Actual» del recálculo y como punto de
   *! partida de «Ajustar totales a mano», así que las tres salían vacías o en cero en todos los
   *! arrendamientos (medido en R-124 y R-188 el 27-sep-2026).
   */
  get amountsTotals(): any {
    const periods = this.chronologicalPeriods
    const suma = (campo: string) => periods.reduce((s: number, p: any) => s + (p?.[campo] || 0), 0)
    const lease: any = this.leaseDetailService.data || {}
    return {
      walletValue: lease.wallet?.walletValue ?? 0,
      walletValueWithoutAccessories: lease.wallet?.walletValueWithoutAccessories ?? 0,
      rentsToCollect: lease.rents?.rentsToCollect ?? 0,
      rentsToCollectAmount: lease.rents?.rentsToCollectAmount ?? 0,
      rentsCollected: lease.rents?.rentsCollected ?? 0,
      rentsCollectedAmount: lease.rents?.rentsCollectedAmount ?? 0,
      capital: suma('capital'),
      interest: suma('interest'),
      accessoriesAmount: suma('accessoriesAmount'),
      accessoriesInterest: suma('accessoriesInterest'),
      inputIva: suma('inputIva'),
      rent: suma('rent'),
      appliedPayment: suma('appliedPayment'),
      payment: suma('payment'),
    }
  }

  get leaseStatus(): string {
    return this.leaseDetailService.data?.status || ''
  }

  // ── Filtros y búsqueda (F8): en memoria sobre el ledger ─────────────────

  setFilter(key: LedgerFilter): void {
    this.filter = key
    this.recompute()
  }

  onSearch(): void {
    this.recompute()
  }

  /** Revela las letras futuras plegadas. La decisión se conserva mientras la pestaña siga abierta. */
  revealFuture(): void {
    this.showFuture = true
    this.recompute()
  }

  private recompute(): void {
    const periods = this.ledger?.periods ?? []
    const unapplied = this.ledger?.unapplied ?? []
    //* Por pago lista `payments[]` (con aplicación al lease, vivos y cancelados) MÁS los pagos del
    //* cliente que todavía no tienen aplicación (`unapplied[]`), como el recién registrado desde la
    //* pestaña: se unen por `_id` para no repetir el PARTIAL que está en las dos listas.
    const payments = [...(this.ledger?.payments ?? [])]
    unapplied.forEach((u) => {
      if (!payments.some((p) => p._id === u._id)) payments.push(u)
    })
    this.twinsById = new Map(unapplied.map((u) => [u._id, u.twins ?? []]))
    const term = this.search.trim().toLowerCase()
    const matches = (s: string | null | undefined) => !!s && s.toLowerCase().includes(term)
    this.filters.forEach((f) => {
      this.filterCounts[f.key] = periods.filter((p) => this.passesFilter(p, f.key)).length
    })
    const filtradas = periods.filter((p) => this.passesFilter(p, this.filter)).filter((p) => !term || p.applications.some((a) => matches(a.trackingKey)) || matches(p.invoice?.active?.folioFiscal))
    this.visiblePeriods = this.foldFuture(filtradas, !!term)
    this.visiblePayments = payments.filter((pay) => !term || matches(pay.trackingKey))
  }

  /** Pliega las futuras que quedan más allá de las primeras filas, salvo que se filtre, se busque o ya se revelaron. */
  private foldFuture(periods: LedgerPeriod[], searching: boolean): LedgerPeriod[] {
    if (this.showFuture || searching || this.filter !== 'all') {
      this.hiddenFutureCount = 0
      return periods
    }
    const visibles = periods.filter((p, i) => i < LeaseEnforceabilityComponent.VISIBLE_ROWS_BEFORE_FOLDING || !this.isFuture(p))
    this.hiddenFutureCount = periods.length - visibles.length
    return visibles
  }

  /** Letra que aún no vence y a la que nadie ha aplicado dinero: no hay nada que operar en ella. */
  isFuture(period: LedgerPeriod): boolean {
    return new Date(period.date).getTime() > Date.now() && period.pending > 0 && !period.applications.some((a) => a.status !== 'CANCELLED')
  }

  //* Semántica de los filtros: «REP pendiente» solo tiene sentido en letras cuya renta ya está cubierta,
  //* porque el complemento se pide al pagar (D21); «Sin factura» incluye la referencia sin archivos;
  //* «Con varios pagos» incluye la letra PARTIAL aunque lleve un solo pago.
  private passesFilter(p: LedgerPeriod, filtro: LedgerFilter): boolean {
    const inv = p.invoice?.active
    switch (filtro) {
      case 'overdue':
        return p.pending > 0 && new Date(p.date).getTime() < Date.now()
      case 'partial':
        return p.view === 'PARTIAL' || p.applications.filter((a) => a.status !== 'CANCELLED').length > 1
      case 'noinv':
        return !inv || (!inv.pdfUrl && !inv.xmlUrl)
      //! `complementStatus` tiene TRES estados y el que interesa es `PENDING`. Preguntar por
      //! «distinto de RECEIVED» dejaba pasar `NOT_REQUIRED` —el CFDI que NO lleva complemento— y la
      //! lista mandaba a Operaciones a perseguir un comprobante que nadie debe emitir. Visto en dev
      //! con una letra PPD marcada NOT_REQUIRED.
      case 'rep':
        return !!inv && inv.paymentType === 'PPD' && inv.complementStatus === 'PENDING' && ['PAID', 'ANTICIPATED', 'MORA_PENDING'].includes(p.view)
      default:
        return true
    }
  }

  // ── Tabla ────────────────────────────────────────────────────────────────

  /**
   * Días de atraso de la letra, contados del CALENDARIO.
   *
   * La columna leía `mora.days`, que con el interruptor de mora apagado vale 0 en TODA letra: una
   * vencida hace 45 días se anunciaba «ATRASADO · 0 días» mientras la franja de arriba, que sí cuenta
   * calendario, decía 135. El atraso es un hecho de la fecha; cobrarlo por él es otra decisión.
   * Una futura no lleva días y devuelve cadena vacía, como el diseño.
   */
  daysLate(period: LedgerPeriod): number | string {
    if (period.paidAt) return period.delayDays || 0
    const dias = Math.floor((Date.now() - new Date(period.date).getTime()) / 86400000)
    if (dias <= 0) return this.isFuture(period) ? '' : 0
    return dias
  }

  /** Pagos vivos que cubrieron la letra: con más de uno la fila lo dice, porque cambia cómo se lee el importe. */
  appliedCount(period: LedgerPeriod): number {
    return period.applications.filter((a) => a.status !== 'CANCELLED').length
  }

  /**
   * El matiz que la etiqueta de estado no alcanza a contar: una letra PAGADA que llegó tarde y una
   * PARCIAL de la que solo se sabe «parcial» son las dos que obligan a abrir el detalle sin este
   * renglón. Devuelve cadena vacía cuando no hay nada que matizar (el `*ngIf as` no la pinta).
   */
  statusNote(period: LedgerPeriod): string {
    if (period.view === 'PAID' && (period.delayDays || 0) > 0) return `+${period.delayDays} d de retraso`
    if (period.view === 'PARTIAL') return `${this.money(period.applied.total)} de ${this.money(period.nominal.rent)}`
    return ''
  }

  viewSeverity(view: string): 'success' | 'danger' | 'info' | 'warning' {
    if (view === 'PAID') return 'success'
    if (view === 'OVERDUE') return 'danger'
    if (view === 'ANTICIPATED') return 'info'
    return 'warning'
  }

  /** Factura vigente de la letra. Mismo nombre que antes para que la columna Factura no cambie:
   *  ahora sale del ledger, que ya la trae poblada por periodo (F2). */
  invoiceFor(period: any): any | null {
    return period?.invoice?.active ?? null
  }

  get moraPaused(): boolean {
    return !!this.ledger && !this.ledger.settings.moraEnabled && this.ledger.settings.offBehavior === 'PAUSE'
  }

  //* Los modales de historial y refacturación resuelven el rastro fiscal por `_id` y listan por número
  //* de letra: los dos índices se rearman con lo que el ledger ya trajo, sin una segunda llamada.
  private indexInvoices(): void {
    const historyNum = new Map<string, any[]>()
    const byId = new Map<string, any>()
    for (const p of this.ledger?.periods ?? []) {
      const todas = [...(p.invoice?.history ?? []), ...(p.invoice?.active ? [p.invoice.active] : [])]
      const pnum = p.n != null ? String(p.n) : ''
      for (const inv of todas) {
        if (inv?._id) byId.set(String(inv._id), inv)
        if (pnum) {
          const arr = historyNum.get(pnum) || []
          arr.push(inv)
          historyNum.set(pnum, arr)
        }
      }
    }
    historyNum.forEach((arr) => arr.sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()))
    this.historyByPeriodNum = historyNum
    this.invoiceById = byId
    this.invoicesLoaded = !!this.ledger
  }

  // ── Cajón del pago (F6) ──────────────────────────────────────────────────

  openPayment(paymentId: string): void {
    //* La evidencia de gemelos solo viaja en `unapplied[]`. Un PARTIAL con aplicación viva y saldo
    //* está en las dos listas: se toma la fila de `payments[]` (destinos) y se le fusionan los `twins`.
    const un = (this.ledger?.unapplied ?? []).find((p) => p._id === paymentId) ?? null
    const row = (this.ledger?.payments ?? []).find((p) => p._id === paymentId) ?? un
    if (!row) return
    this.drawerPayment = { ...row, twins: un?.twins ?? row.twins ?? [] }
    this.showDrawer = true
  }

  // ── Aplicar · cancelar · residual (F11-F14, F20) ──────────────────────────

  //* La fecha de la primera letra por cobrar: el diálogo la enseña para explicar el orden FIFO.
  get nextToCollectDate(): string | null {
    const n = this.ledger?.nextToCollect
    return n == null ? null : ((this.ledger?.periods ?? []).find((p) => p.n === n)?.date ?? null)
  }

  openApply(payments: PaymentRow[]): void {
    this.previewMode = 'APPLY'
    this.previewPayments = payments
    this.previewApplication = null
    this.showPreview = true
    this.loadClientLeases()
  }

  /**
   * Los otros arrendamientos activos del cliente, para poder repartir el pago entre varios (D3).
   *
   * Se piden al abrir el diálogo y no al cargar la pestaña: la inmensa mayoría de las veces nadie lo abre,
   * y son siete consultas que no hacen falta para ver el libro mayor.
   *
   * Si la llamada falla, `clientLeases` queda vacío y el diálogo se comporta como siempre —un solo
   * arrendamiento, sin selector—: perder el reparto es molesto, no poder aplicar sería peor.
   */
  private loadClientLeases(): void {
    const clientId = (this.ledger?.lease?.client as any)?._id ?? (this.ledger?.lease?.client as any)
    if (!clientId) {
      this.clientLeases = []
      return
    }

    this.apiLease.getActiveByClient(String(clientId)).subscribe((leases) => {
      //* La mora sale del estado que ya publica el libro mayor, no de una constante: el día que el
      //* interruptor se encienda, la capacidad de cada arrendamiento la incluye sin tocar este código.
      this.clientLeases = toAllocatableLeases(leases, !this.moraPaused)
    })
  }

  openResidual(payments: PaymentRow[]): void {
    this.previewMode = 'RESIDUAL'
    this.previewPayments = payments
    this.previewApplication = null
    this.showPreview = true
  }

  openCancelApplication(e: { applicationId: string; paymentId: string; n: number | null; letters?: number[]; amount: number }): void {
    this.previewMode = 'CANCEL'
    this.previewPayments = (this.ledger?.payments ?? []).filter((p) => p._id === e.paymentId)
    this.previewApplication = { applicationId: e.applicationId, n: e.n, letters: e.letters ?? [], amount: e.amount }
    this.showPreview = true
  }

  /** Corre el dryRun: el backend calcula todo con el mismo código y aborta la transacción. */
  preview(e: { mode: PreviewMode; paymentId: string; applicationId?: string; payload: any }, modal: ApplyPreviewDialogComponent): void {
    this.previewing = true
    const req: Observable<{ error: boolean; msg: string; data: any }> =
      e.mode === 'CANCEL' ? this.apiApplicationService.cancelWithReason(e.applicationId as string, e.payload, true) : this.apiApplicationService.createPreview(e.payload, true)
    req.subscribe((r) => {
      this.previewing = false
      if (r.error) {
        modal.setPreview(null, r.msg || 'No se pudo calcular el impacto.')
        return
      }
      modal.setPreview(r.data)
    })
  }

  confirm(e: { mode: PreviewMode; paymentId: string; applicationId?: string; payload: any }, modal: ApplyPreviewDialogComponent): void {
    this.savingPreview = true
    const req: Observable<{ error: boolean; msg: string; data: any }> =
      e.mode === 'CANCEL' ? this.apiApplicationService.cancelWithReason(e.applicationId as string, e.payload, false) : this.apiApplicationService.createPreview(e.payload, false)
    req.subscribe((r) => {
      this.savingPreview = false
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'No se pudo aplicar', detail: r.msg, life: 8000 })
        //* Huella caducada: el arrendamiento cambió entre previsualizar y aplicar, así que se
        //* recalcula contra el estado nuevo en vez de reintentar a ciegas.
        modal.invalidatePreview()
        return
      }
      modal.hide()
      this.showDrawer = false
      this.messageService.add({
        severity: 'success',
        summary: e.mode === 'CANCEL' ? 'Aplicación cancelada' : 'Pago aplicado',
        detail: this.confirmSummary(e.mode, r.data),
        life: 6000,
      })
      //* Solo el libro mayor: `getLeaseDetail()` destruiría la pestaña entera.
      this.ledgerService.refresh()
    })
  }

  /**
   * El resumen del toast. **«Cubierta» se cuenta por el estado resultante de cada letra, no por el
   * tamaño del plan**: un abono parcial toca una letra y no la cubre, y el mensaje decía «1 letra(s)
   * cubierta(s)» justo después de que la previsualización avisara «la letra queda parcial». Medido en
   * dev aplicando $500 a una letra con $2,000 pendientes.
   */
  private confirmSummary(mode: PreviewMode, data: any): string {
    if (mode === 'CANCEL') return `${data?.reopened?.length ?? 0} letra(s) reabierta(s)`
    const plan: Array<{ resulting?: { status?: string } }> = data?.plan ?? []
    const cubiertas = plan.filter((p) => p.resulting?.status === 'PAID').length
    const sobrante = Number(data?.leftover ?? 0)
    const base = cubiertas === plan.length ? `${plan.length} letra(s) cubierta(s)` : `${plan.length} letra(s) tocada(s) · ${cubiertas} cubierta(s)`
    return sobrante > 0 ? `${base} · queda disponible ${this.money(sobrante)}` : base
  }

  //* El pipe de moneda no aplica en TS: lo consumen los toasts y el sub-renglón de la tabla.
  private money(n: number): string {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0)
  }

  // ── Registrar un pago desde la pestaña (F19) ──────────────────────────────

  //* Desde aquí el cliente ya está decidido: el formulario lo fija y lo deshabilita.
  get fixedClient(): { _id: string; name: string } | null {
    const c = this.ledger?.lease.client
    return c ? { _id: c._id, name: c.name } : null
  }

  //* `PaymentService.entity` está declarado como si siempre existiera, pero no se construye hasta
  //* `newEntity()`. Este getter dice la verdad y evita que la plantilla confíe en algo que puede faltar.
  get canSavePayment(): boolean {
    const entity: any = this.paymentService.entity
    return !!entity?.valid && !this.savingPayment
  }

  openRegister(): void {
    //* `entity` no existe hasta `newEntity()`: se construye ANTES de montar el formulario.
    this.paymentService.newEntity()
    this.duplicate = null
    this.duplicateReason = ''
    this.confirmingCloseRegister = false
    this.showRegister = true
  }

  //* La X y Esc del diálogo van apagados: toda salida pasa por aquí para no perder la captura sin avisar.
  requestCloseRegister(): void {
    const capturado = !!this.paymentService.entity?.dirty || !!this.duplicate
    if (capturado && !this.confirmingCloseRegister) {
      this.confirmingCloseRegister = true
      return
    }
    this.closeRegister()
  }

  closeRegister(): void {
    this.confirmingCloseRegister = false
    this.showRegister = false
    this.duplicate = null
    this.duplicateReason = ''
  }

  savePayment(confirmDuplicate = false): void {
    this.savingPayment = true
    const extra = confirmDuplicate ? { confirmDuplicate: true, duplicateReason: this.duplicateReason.trim() } : {}
    this.paymentService.save(extra).subscribe((r) => {
      this.savingPayment = false
      if (r.error && r.code === 'POSSIBLE_DUPLICATE') {
        //* Avisar sin bloquear: la evidencia se pinta y la persona decide si registra de todos modos.
        this.duplicate = r.detail ?? null
        return
      }
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'Error al crear el pago', detail: r.msg, life: 8000 })
        return
      }
      this.showRegister = false
      this.duplicate = null
      this.messageService.add({ severity: 'success', summary: 'Pago registrado', detail: 'Ya puedes aplicarlo a las letras.', life: 6000 })
      //* Se ofrece aplicarlo enseguida: el pago nuevo llega en `unapplied[]` con el refresco y en cuanto
      //* aparece se abre el diálogo con él ya elegido. La suscripción va ANTES de `refresh()` porque el
      //* BehaviorSubject reemite el libro viejo (sin ese id) y el filtro lo deja pasar.
      const nuevoId: string | undefined = r.data?._id
      if (nuevoId) {
        this.subs.push(
          this.ledgerService.ledger$
            .pipe(
              filter((l): l is LeaseLedger => !!l && l.unapplied.some((p) => p._id === nuevoId)),
              take(1),
            )
            .subscribe((l) => this.openApply(l.unapplied.filter((p) => p._id === nuevoId))),
        )
      }
      this.ledgerService.refresh()
    })
  }

  // ── Ajustar el pago desde el cajón ──────────────────────────────────────

  /**
   * Abre el ajuste con las operaciones que tienen sentido para ESTE pago.
   *
   * El modal decide qué ofrecer a partir del grupo de la bandeja de «Pagos por atender»; desde el
   * cajón no venimos de ninguna bandeja, así que el grupo se deriva del estado del pago: uno anulado
   * solo admite deshacer; uno vivo con saldo por aplicar se trata como «posible duplicado», que
   * ofrece anular **sin preseleccionarlo** y enseña los pagos iguales del cliente — aquí el botón
   * dice «Ajustar», no «Anular», así que llegar con la acción destructiva marcada sería una trampa.
   * Sin saldo y vigente sí venimos de una intención explícita de anular, como en el listado.
   */
  openAdjust(payment: PaymentRow): void {
    //* El modal viene de la bandeja de «Pagos por atender» y lee la forma de SUS filas —`cliente`,
    //* `monto`, `fecha`—, que no es la del libro mayor. Sin esta traducción su cabecera dice
    //* «Sin cliente» y se queda sin monto ni fecha.
    this.adjustPayment = {
      ...payment,
      cliente: this.ledger?.lease?.client?.name,
      monto: payment.amount,
      fecha: payment.paidOn,
    } as any
    this.adjustGroup = payment.deletedAt ? 'anulados' : payment.pendingToApply > 0 ? 'posiblesDuplicados' : 'listado'
    this.showAdjustModal = true
  }

  /** La fecha que se corrige es la del depósito (`paidOn`), no la de captura. */
  openChangeDate(payment: PaymentRow): void {
    this.datePayment = { _id: payment._id, fecha: payment.paidOn, monto: payment.amount, trackingKey: payment.trackingKey, cliente: this.ledger?.lease?.client?.name }
    this.showDateDialog = true
  }

  /** Estado, aplicaciones y bitácora del pago: lo que el modal usa para explicar el ajuste. */
  loadAdjustContext(paymentId: string, modal: any): void {
    this.apiPaymentService.getPaymentContext(paymentId).subscribe((r) => {
      modal.setContext(r.error ? null : r.data)
      if (r.error) this.messageService.add({ severity: 'warn', summary: 'Información del pago', detail: r.msg, life: 5000 })
    })
  }

  /** Corre el dryRun: el backend calcula todo y aborta la transacción, así que no escribe nada. */
  previewAdjust(e: { paymentId: string; payload: any }, modal: any): void {
    this.adjustPreviewing = true
    this.apiPaymentService.adjustPayment(e.paymentId, e.payload, true).subscribe((r) => {
      this.adjustPreviewing = false
      if (r.error) {
        modal.setPreview(null, r.msg || 'No se pudo calcular el impacto del ajuste.')
        return
      }
      modal.setPreview(r.data)
    })
  }

  applyAdjust(e: { paymentId: string; payload: any }, modal: any): void {
    this.adjustSaving = true
    this.apiPaymentService.adjustPayment(e.paymentId, e.payload, false).subscribe((r) => {
      this.adjustSaving = false
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'No se pudo ajustar', detail: r.msg, life: 8000 })
        //* Si el pago cambió entre la previsualización y el guardado, el backend responde 409: hay
        //* que volver a calcular contra el estado nuevo en lugar de reintentar a ciegas.
        modal.invalidatePreview()
        return
      }
      this.messageService.add({ severity: 'success', summary: 'Pago ajustado', detail: 'El cambio quedó en la bitácora del pago.', life: 6000 })
      modal.hide()
      this.showDrawer = false
      //* El ajuste mueve el disponible del pago y puede reabrir letras: se vuelve a leer el libro.
      this.ledgerService.refresh()
    })
  }

  // ── Condonar desde la letra (F28): abre el editor con esa letra ya elegida ──

  openWaive(n: number): void {
    this.presetWaivePeriod = n
    this.openAmountsEditor()
  }

  openAmountsEditor(): void {
    this.showAmountsModal = true
  }

  previewAmounts(payload: any, modal: any): void {
    this.previewingAmounts = true
    this.leaseDetailService.previewLeaseAmounts(payload).subscribe((r) => {
      this.previewingAmounts = false
      if (r.error) {
        modal.setPreview(null, r.msg || 'No se pudo calcular el impacto del ajuste.')
        return
      }
      modal.setPreview(r.data)
    })
  }

  applyAmounts(payload: any, modal: any): void {
    this.savingAmounts = true
    this.leaseDetailService.applyLeaseAmounts(payload).subscribe((r) => {
      this.savingAmounts = false
      if (r.error) {
        this.messageService.add({ severity: 'error', summary: 'No se pudo aplicar', detail: r.msg, life: 8000 })
        //* Si el arrendamiento cambió entre la previsualización y el guardado, el backend responde
        //* 409: hay que volver a calcular contra el estado nuevo en lugar de reintentar a ciegas.
        modal.invalidatePreview()
        return
      }
      this.showAmountsModal = false
      const letras = r.data?.periodDiffs?.length ?? 0
      const deltaCartera = r.data?.deltas?.walletValue
      const detalle = [letras ? `${letras} letra(s) actualizada(s)` : null, deltaCartera != null ? `cartera ${deltaCartera > 0 ? '+' : ''}${this.formatMoney(deltaCartera)}` : null]
        .filter(Boolean)
        .join(' · ')
      this.messageService.add({ severity: 'success', summary: 'Saldos actualizados', detail: detalle || 'El ajuste se aplicó correctamente.', life: 6000 })
      //* El editor sí recarga el detalle: la Bitácora necesita el `auditTrail` nuevo y la pestaña se
      //* re-crea de todos modos. El ledger se vuelve a pedir en el `ngOnInit` de la pestaña nueva.
      this.leaseDetailService.getLeaseDetail()
    })
  }

  private formatMoney(n: number): string {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0)
  }

  // ── Facturas por periodo ──

  isDownloading(period: any, kind: 'pdf' | 'xml'): boolean {
    const inv = this.invoiceFor(period)
    return !!inv && this.downloadingKey === `${inv._id}-${kind}`
  }

  downloadInvoiceFile(period: any, kind: 'pdf' | 'xml'): void {
    const inv = this.invoiceFor(period)
    if (!inv?._id) return
    this.downloadingKey = `${inv._id}-${kind}`
    this.apiInvoiceService.downloadFile(inv._id, kind).subscribe({
      next: (res) => {
        this.downloadingKey = null
        const blob = res.body as Blob | null
        if (!blob) {
          this.toastErr('El servidor no devolvió contenido.')
          return
        }
        const filename = this.filenameFromResponse(res) || `${inv.folioFiscal || inv.uid || inv._id}.${kind}`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      },
      error: () => {
        this.downloadingKey = null
        this.toastErr(`No se pudo descargar el ${kind.toUpperCase()} de la factura.`)
      },
    })
  }

  private filenameFromResponse(res: any): string | null {
    const cd = res?.headers?.get?.('content-disposition') || res?.headers?.get?.('Content-Disposition')
    if (!cd) return null
    const star = /filename\*=UTF-8''([^;]+)/i.exec(cd)
    if (star) return decodeURIComponent(star[1])
    const m = /filename="?([^";]+)"?/i.exec(cd)
    return m ? m[1] : null
  }

  // ── Refacturar ──
  openRefacturar(period: any): void {
    const inv = this.invoiceFor(period)
    if (!inv?._id) return
    this.refacturarInvoiceId = inv._id
    this.refacturarCurrentFolioFiscal = inv.folioFiscal || ''
    this.refacturarNewFolioFiscal = ''
    this.refacturarReason = ''
    this.refacturarPdf = null
    this.refacturarXml = null
    this.showRefacturarModal = true
  }

  onRefacturarPdf(e: Event): void {
    this.refacturarPdf = (e.target as HTMLInputElement).files?.[0] || null
  }
  onRefacturarXml(e: Event): void {
    this.refacturarXml = (e.target as HTMLInputElement).files?.[0] || null
  }

  confirmRefacturar(): void {
    if (!this.refacturarInvoiceId) return
    const files: File[] = []
    if (this.refacturarPdf) files.push(this.refacturarPdf)
    if (this.refacturarXml) files.push(this.refacturarXml)
    // El Folio Fiscal se deriva de los archivos si se adjuntan; si no, hay que teclearlo.
    const folioFiscal = this.refacturarNewFolioFiscal.trim()
    if (!folioFiscal && !files.length) return
    this.refacturando = true
    this.apiInvoiceService
      .refacturar(this.refacturarInvoiceId, { newFolioFiscal: folioFiscal || undefined, reason: this.refacturarReason.trim() || undefined, userId: this.userId }, files.length ? files : undefined)
      .subscribe({
        next: (r) => {
          this.refacturando = false
          if (r.error) {
            this.toastErr(r.msg)
            return
          }
          this.showRefacturarModal = false
          this.messageService.add({ severity: 'success', summary: 'Factura refacturada', detail: 'Se creó la nueva factura y se conservó el historial.', life: 5000 })
          //* Solo el ledger: trae la factura nueva y su historial, y recargar el detalle completo
          //* destruiría la pestaña con las letras expandidas y el filtro puestos (§8.2).
          this.ledgerService.refresh()
        },
        error: () => {
          this.refacturando = false
          this.toastErr('No se pudo refacturar la factura.')
        },
      })
  }

  // ── Cancelar ──
  openCancel(period: any): void {
    const inv = this.invoiceFor(period)
    if (!inv?._id) return
    this.cancelInvoiceId = inv._id
    this.cancelFolioFiscal = inv.folioFiscal || ''
    this.cancelReason = ''
    this.showCancelModal = true
  }

  confirmCancel(): void {
    if (!this.cancelInvoiceId || !this.cancelReason.trim()) return
    this.cancelando = true
    this.apiInvoiceService.cancel(this.cancelInvoiceId, { reason: this.cancelReason.trim(), userId: this.userId }).subscribe({
      next: (r) => {
        this.cancelando = false
        if (r.error) {
          this.toastErr(r.msg)
          return
        }
        this.showCancelModal = false
        this.messageService.add({ severity: 'success', summary: 'Factura cancelada', detail: 'La factura quedó cancelada (historial conservado).', life: 5000 })
        //* Solo el ledger (§8.2).
        this.ledgerService.refresh()
      },
      error: () => {
        this.cancelando = false
        this.toastErr('No se pudo cancelar la factura.')
      },
    })
  }

  // ── Historial de facturación del periodo ──
  // El botón de historial solo se muestra si el periodo tiene facturas canceladas o reemplazadas.
  hasCancelledHistory(period: any): boolean {
    const arr = this.historyByPeriodNum.get(String(period?.n))
    return !!arr && arr.some((inv) => inv.active === false || inv.status === 'CANCELLED' || inv.status === 'REPLACED')
  }

  openHistory(period: any): void {
    this.historyPeriodNumber = period?.n ?? null
    this.historyInvoices = (this.historyByPeriodNum.get(String(period?.n)) || []).slice()
    this.showHistoryModal = true
  }

  // Folio Fiscal de una factura ligada (replaces/replacedBy) para mostrar el rastro fiscal.
  folioFiscalOf(invoiceRef: any): string {
    if (!invoiceRef) return ''
    return this.invoiceById.get(String(invoiceRef?._id || invoiceRef))?.folioFiscal || ''
  }

  historyStatusLabel(status: string): string {
    const m: Record<string, string> = { PENDING_FILES: 'Pendiente', LINKED: 'Vigente', REPLACED: 'Reemplazada', CANCELLED: 'Cancelada' }
    return m[status] || status || '—'
  }
  historyStatusSeverity(status: string): string {
    const m: Record<string, string> = { PENDING_FILES: 'warning', LINKED: 'success', REPLACED: 'info', CANCELLED: 'danger' }
    return m[status] || 'info'
  }

  isDownloadingInvoice(inv: any, kind: 'pdf' | 'xml'): boolean {
    return !!inv && this.downloadingKey === `${inv._id}-${kind}`
  }

  // Descarga por _id de factura (sirve para el historial: también facturas canceladas/reemplazadas).
  downloadHistoryFile(inv: any, kind: 'pdf' | 'xml'): void {
    if (!inv?._id) return
    this.downloadingKey = `${inv._id}-${kind}`
    this.apiInvoiceService.downloadFile(inv._id, kind).subscribe({
      next: (res) => {
        this.downloadingKey = null
        const blob = res.body as Blob | null
        if (!blob) {
          this.toastErr('El servidor no devolvió contenido.')
          return
        }
        const filename = this.filenameFromResponse(res) || `${inv.folioFiscal || inv.uid || inv._id}.${kind}`
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      },
      error: () => {
        this.downloadingKey = null
        this.toastErr(`No se pudo descargar el ${kind.toUpperCase()} de la factura.`)
      },
    })
  }

  // ── Cargar factura del periodo (carga unitaria) ──
  // Permiso de escritura de facturas (mismo origen que *appIfHasPermission).
  get canUploadInvoice(): boolean {
    return this.authService.hasModulePermission('invoices', 'create')
  }

  openUpload(period: any): void {
    this.uploadPeriod = period
    this.uploadPdf = null
    this.uploadXml = null
    this.showUploadModal = true
  }
  onUploadPdf(e: Event): void {
    this.uploadPdf = (e.target as HTMLInputElement).files?.[0] || null
  }
  onUploadXml(e: Event): void {
    this.uploadXml = (e.target as HTMLInputElement).files?.[0] || null
  }

  confirmUpload(): void {
    const leaseId = this.leaseDetailService.id
    const periodId = this.uploadPeriod?._id
    if (!leaseId || !periodId) return
    const files: File[] = []
    if (this.uploadPdf) files.push(this.uploadPdf)
    if (this.uploadXml) files.push(this.uploadXml)
    if (!files.length) {
      this.toastErr('Selecciona al menos el PDF o el XML.')
      return
    }
    this.uploadingInvoice = true
    this.apiInvoiceService.uploadForPeriod(periodId, leaseId, files, this.userId).subscribe({
      next: (r) => {
        this.uploadingInvoice = false
        if (r.error) {
          this.toastErr(r.msg)
          return
        }
        this.showUploadModal = false
        this.messageService.add({
          severity: 'success',
          summary: 'Factura cargada',
          detail: r.data?.warning ? `Ligada al periodo · ⚠ ${r.data.warning}` : 'La factura quedó ligada al periodo.',
          life: 5000,
        })
        //* Solo el ledger (§8.2).
        this.ledgerService.refresh()
      },
      error: () => {
        this.uploadingInvoice = false
        this.toastErr('No se pudo cargar la factura.')
      },
    })
  }

  // ── Complementos de pago (REP) del periodo ──
  // Abre el modal reutilizado con la factura vigente del periodo. El modal lista los REP ligados
  // (descarga PDF/XML) y, con permiso `invoices.create`, permite subir nuevos (se ligan por UUID).
  openComplements(period: any): void {
    const inv = this.invoiceFor(period)
    if (!inv?._id) return
    this.complementsInvoice = inv
    this.showComplementsModal = true
  }

  // Tras ligar/subir un REP, refresca el detalle (period.activeInvoice.complementStatus) y las
  // facturas para que el icono ámbar → verde se actualice sin recargar la página.
  onComplementsChanged(): void {
    //* Solo el ledger: el icono ámbar → verde sale de `invoice.complementStatus` del libro (§8.2).
    this.ledgerService.refresh()
  }

  private toastErr(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Factura', detail, life: 6000 })
  }

  openEditStartDate(): void {
    this.showEditModal = true
  }

  saveStartDate(payload: { startDate: string; reason: string; confirmPaidPeriods: boolean }): void {
    this.saving = true
    this.leaseDetailService.editAmortizationStartDate(payload.startDate, payload.reason, payload.confirmPaidPeriods).subscribe((r) => {
      this.saving = false
      if (!r.error) {
        this.showEditModal = false
        this.messageService.add({
          severity: 'success',
          summary: 'Fechas actualizadas',
          detail: r.msg || 'La tabla de amortización se recalculó en cascada.',
          life: 4000,
        })
        // Las ligas de factura sobreviven el recalc (mismo _id), pero recargamos por consistencia.
        this.ledgerService.refresh()
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'No se pudo actualizar',
          detail: r.msg,
          life: 6000,
        })
      }
    })
  }
}
